/**
 * 69.2b, the relay (decision 145 (3); DIRECTION §0.8, 69.2's third need: "a buildable structure kind on the beacon's
 * pattern, with sight round it and a link range"). A squad plants one on the build path (core/build.ts); it stands on the
 * R12 body table with its health, and while it stands it sees round it: living enemies on its planet within its sight
 * radius whose person eye it has a clear line to (terrain by `personLineOfSight`, props by `segmentCovered`, the checks a
 * person's sight and a tower's use). Unlike a tower's radar dot, a relay's sighting names the unit, as a drone's does. Each
 * relay looks on the rate table's beat (match/rates.ts `RATES.relaySight`, the towers' and the bots' five a second), on its
 * own tick of it (its id's phase: ten relays at 25 a side cost 3.1 ms looking at once; spread, the worst tick is 1.5 ms,
 * one relay with the whole enemy team in view at about 40 µs a sight line, and the mean 0.13–0.27 ms a tick), and its
 * look joins the team's sightings (`state.sightings`, by 'relay'); a destroyed relay sees nothing and its last age out,
 * and a relay its side's web does not link to the HQ (69.2c) sees nothing either.
 *
 * The ranges are dials in people (constants RELAY), here in world units at the match's scale. `relayLink` is the range the
 * side's web links within (69.2c, lane D). Whether a relay sees is asked of `relaySees` alone, so the web can add "only
 * while linked" in one place. Pure; nothing runs without GameConfig.relays.
 */
import type { GameState, StructureState, Sighting, Vec3 } from '../types';
import { RELAY } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { structureTeam } from './body';
import { eyePointAt } from './los';
import { segmentCovered } from './bullets';
import { personEyePoint, personLineOfSight } from './fineTerrain';
import { mergeSightings } from './towers';

/** The radius a relay sees round it, in world units at this scale. */
export function relaySight(state: Pick<GameState, 'config'>): number {
  return RELAY.sight * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** The range within which two of a side's structures link (69.2c's web reads it), in world units at this scale. */
export function relayLink(state: Pick<GameState, 'config'>): number {
  return RELAY.link * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** A standing relay — or a forward base (69.4: it sees as a relay does, on the same gate). */
export function isStandingRelay(s: Pick<StructureState, 'type' | 'health'>): boolean {
  return (s.type === 'relay' || s.type === 'fob') && s.health > 0;
}

/**
 * Does this relay see now? A standing relay of a side, while its side's web links it to the HQ (69.2c: `state.webs`,
 * core/web.ts; before the web's first pass, a standing relay sees).
 */
export function relaySees(state: Pick<GameState, 'players'> & Partial<Pick<GameState, 'webs'>>, s: StructureState): boolean {
  if (!isStandingRelay(s)) return false;
  const team = structureTeam(state, s);
  if (team === null) return false;
  const web = state.webs?.[team];
  return !web || web.linked.includes(s.id);
}

/** The relay's eye: RELAY.eye people above the ground it stands on. */
export function relayEyePoint(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'planetIndex' | 'surfaceDir'> & Partial<Pick<StructureState, 'baseLift'>>): Vec3 {
  return eyePointAt(state.planets[s.planetIndex], s.surfaceDir, RELAY.eye * PERSON_BODY_HEIGHT * personSetOf(state).k + (s.baseLift ?? 0));   // (67.52: over its base)
}

/** Every sighting the seeing relays make now, at `tick`: each enemy a relay sees, named. `due`: which relays look this tick (all, by default). */
export function relaySightingsNow(state: GameState, tick: number, due: (relay: StructureState) => boolean = () => true): Sighting[] {
  const out: Sighting[] = [];
  const ps = personSetOf(state), reach = relaySight(state);
  for (const relay of state.structures) {
    if (!relaySees(state, relay) || !due(relay)) continue;
    const team = structureTeam(state, relay)!;
    const planet = state.planets[relay.planetIndex];
    if (!planet) continue;
    const eye = relayEyePoint(state, relay);
    for (const u of state.players) {
      if (!u.alive || (u.landingLeft ?? 0) > 0 || u.boarded || u.teamIndex === team || u.planetIndex !== relay.planetIndex) continue;
      const target = personEyePoint(u, state.planets, ps, ps.losEyeHeight);
      const dx = target.x - eye.x, dy = target.y - eye.y, dz = target.z - eye.z;
      if (dx * dx + dy * dy + dz * dz > reach * reach) continue;
      if (!personLineOfSight(eye, target, state) || segmentCovered(eye, target, state, undefined, ps.coverSampleStep)) continue;
      out.push({ teamIndex: team, unitId: u.id, unitTeam: u.teamIndex, planetIndex: u.planetIndex, surfaceDir: { ...u.surfaceDir }, pos: personEyePoint(u, state.planets, ps, 0), tick, by: 'relay', sourceId: relay.id });
    }
  }
  return out;
}

/** The relays' look this tick, folded into the state's sightings (named: the newest per team and unit wins). */
export function updateRelaySightings(state: GameState, tick: number, due?: (relay: StructureState) => boolean): GameState {
  return mergeSightings(state, relaySightingsNow(state, tick, due));
}
